import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { CrmButton, CrmCard, CrmSelect } from '../ui';
import { listStorefronts } from '../../services/commercialContextService';
import { experiencePath } from '../../services/storefrontExperience';
import './storefront-experience.scss';

const StorefrontContext = createContext(null);
const isActiveStorefront = (storefront) => ![false, 0, '0', 'false'].includes(storefront?.is_active);
export const useExperienceStorefront = () => useContext(StorefrontContext);

const groups = [
  { label: 'Overview', icon: <DashboardOutlinedIcon fontSize="small" />, items: [{ path: '', label: 'Overview', tab: 'overview' }] },
  { label: 'Content', icon: <ArticleOutlinedIcon fontSize="small" />, items: [{ path: '/content/pages', label: 'Pages', tab: 'pages' }, { path: '/content/blogs', label: 'Blogs', tab: 'blogs' }, { path: '/content/popups', label: 'Popups', tab: 'popups' }] },
  { label: 'Navigation & Layout', icon: <MenuOutlinedIcon fontSize="small" />, items: [{ path: '/navigation/header', label: 'Header', tab: 'header' }, { path: '/navigation/menus', label: 'Menus', tab: 'menus' }, { path: '/navigation/footer', label: 'Footer', tab: 'footer' }] },
  { label: 'Integrations', icon: <ExtensionOutlinedIcon fontSize="small" />, items: [{ path: '/integrations', label: 'Integrations', tab: 'integrations' }] },
  { label: 'Developer', icon: <CodeOutlinedIcon fontSize="small" />, items: [{ path: '/developer', label: 'Developer & API', tab: 'developer' }] },
  { label: 'Configuration', icon: <SettingsOutlinedIcon fontSize="small" />, items: [{ path: '/configuration', label: 'Configuration', tab: 'configuration' }] },
];

const allItems = groups.flatMap((group) => group.items);
const itemForPath = (activePath) => {
  if (activePath.startsWith('/content/page/')) return itemForTab('pages') || allItems[0];
  return allItems.find((item) => item.path === '' ? activePath === '' : activePath === item.path || activePath.startsWith(`${item.path}/`)) || allItems[0];
};
const itemForTab = (tab) => allItems.find((item) => item.tab === tab) || null;

export default function StorefrontExperience({ children }) {
  const { storefrontKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [storefronts, setStorefronts] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    listStorefronts().then((data) => {
      if (!active) return;
      const rows = Array.isArray(data) ? data : data.items || [];
      setStorefronts(rows.filter(isActiveStorefront));
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.error || 'Could not load storefronts.');
    });
    return () => { active = false; };
  }, []);

  const selected = storefronts?.find((storefront) => storefront.code === storefrontKey || String(storefront.id) === storefrontKey);
  const activePath = useMemo(() => location.pathname.replace(/^\/storefronts\/[^/]+\/experience/, '') || '', [location.pathname]);
  const activeItem = useMemo(() => itemForPath(activePath), [activePath]);

  useEffect(() => {
    if (!storefronts || storefrontKey || !location.pathname.startsWith('/website')) return;
    const requested = new URLSearchParams(location.search).get('storefront');
    const target = requested
      ? storefronts.find((storefront) => storefront.code === requested || String(storefront.id) === requested)
      : storefronts.find((storefront) => storefront.code === 'FUSION_BOX_WEB');
    if (target) {
      const legacyPath = location.pathname.replace(/^\/website/, '') || '';
      const legacyMap = {
        '/pages': '/content/pages', '/blogs': '/content/blogs', '/popup': '/content/popups',
        '/header': '/navigation/header', '/footer': '/navigation/footer', '/settings': '/configuration',
      };
      const canonicalPath = legacyMap[legacyPath] || '';
      const legacyItem = itemForPath(canonicalPath);
      navigate(`${experiencePath(legacyItem.path, target.code)}?tab=${legacyItem.tab}`, { replace: true });
    } else setError('Choose a storefront to open it.');
  }, [storefronts, storefrontKey, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!selected?.code) return;
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const requestedItem = itemForTab(requestedTab);

    if (requestedItem && requestedItem.path !== activeItem.path) {
      params.set('tab', requestedItem.tab);
      navigate(`${experiencePath(requestedItem.path, selected.code)}?${params.toString()}`, { replace: true });
      return;
    }

    if (requestedTab !== activeItem.tab) {
      params.set('tab', activeItem.tab);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [activeItem, location.pathname, location.search, navigate, selected?.code]);

  const navigateToTab = (item) => {
    if (!selected?.code) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', item.tab);
    navigate(`${experiencePath(item.path, selected.code)}?${params.toString()}`);
  };

  const changeStorefront = (code) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeItem.tab);
    navigate(`${experiencePath(activeItem.path, code)}?${params.toString()}`);
  };

  if (!storefronts) {
    return <div className="storefront-experience__loading">{error ? <div className="storefront-experience__alert is-error">{error}</div> : <span className="crm-button__spinner" aria-label="Loading storefronts" />}</div>;
  }

  return (
    <StorefrontContext.Provider value={selected || null}>
      <div className="storefront-experience">
        <CrmCard className="storefront-experience__header-card" bodyClassName="storefront-experience__header-body">
          <div className="storefront-experience__identity">
            <h1>{selected?.name || 'Storefront'}</h1>
            <p>{selected ? `${selected.domain || 'No domain'} · ${selected.code}` : 'Choose a storefront.'}</p>
          </div>
          <div className="storefront-experience__header-actions">
            <CrmSelect aria-label="Storefront" value={selected?.code || ''} onChange={(event) => changeStorefront(event.target.value)}>
              <option value="" disabled>Choose storefront</option>
              {storefronts.map((storefront) => <option key={storefront.id} value={storefront.code}>{storefront.name}</option>)}
            </CrmSelect>
            <CrmButton variant="secondary" onClick={() => navigate('/storefronts')}>All Storefronts</CrmButton>
          </div>
          {error ? <div className="storefront-experience__alert is-error">{error}</div> : null}
          {storefrontKey && !selected ? <div className="storefront-experience__alert is-error">Storefront not found.</div> : null}
        </CrmCard>

        {selected ? (
          <div className="storefront-experience__layout">
            <aside className="storefront-experience__sidebar" aria-label="Storefront experience navigation">
              {groups.map((group, index) => (
                <section className="storefront-experience__nav-group" key={group.label}>
                  {index > 0 ? <div className="storefront-experience__divider" /> : null}
                  <div className="storefront-experience__nav-heading">{group.icon}<span>{group.label}</span></div>
                  <div className="storefront-experience__nav-items">
                    {group.items.map((item) => (
                      <button
                        key={item.tab}
                        type="button"
                        className={`storefront-experience__nav-item ${activeItem.tab === item.tab ? 'is-active' : ''}`}
                        onClick={() => navigateToTab(item)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </aside>
            <main className="storefront-experience__content"><React.Fragment key={selected.code}>{children}</React.Fragment></main>
          </div>
        ) : null}
      </div>
    </StorefrontContext.Provider>
  );
}
